import { WalletPayment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { parse, format, isValid as isValidDate } from 'date-fns';
import React from 'react';
import { Link } from 'react-router-dom';

import PencilEditIcon from '../../assets/icons/PencilEdit';
import colors from '../../constants/colors';
import paymentTypeMap from '../../constants/paymentTypeNames';

interface PaymentMethodOptionButtonProps {
  account: WalletPayment;
}

const formatExpirationDate = (expirationDate?: string): string => {
  if (!expirationDate) return '';
  const parsed = parse(expirationDate, 'MM/dd/yyyy', new Date());
  return parsed && isValidDate(parsed) ? format(parsed, 'MM/yy') : '';
};

const PaymentMethodOptionButton = ({
  account,
}: PaymentMethodOptionButtonProps) => {
  const isCreditCard = !!account?.card;

  const getButtonText = () => {
    if (account.shortName) {
      return account.shortName;
    }

    if (account.card) {
      return `${paymentTypeMap[account.card.type]} ****${account.card.number}`;
    } else if (account.account) {
      return `${paymentTypeMap[account.account.type]} ****${
        account.account.number
      }`;
    }
  };

  const getAriaLabelText = () => {
    if (isCreditCard) {
      const expirationDate = formatExpirationDate(
        account?.card?.expirationDate
      );
      return `Edit payment method for ${
        account?.shortName
          ? account?.shortName
          : paymentTypeMap[account?.card?.type]
      } ****${account?.card?.number} expiration date ${expirationDate}`;
    } else {
      return `Edit payment method for ${
        account?.shortName
          ? account?.shortName
          : paymentTypeMap?.[account?.account?.type]
      } ****${account?.account?.number}`;
    }
  };

  return (
    <ButtonBase
      aria-label={getAriaLabelText()}
      component={Link}
      relative="path"
      to="edit"
      state={{
        walletAccount: account,
      }}
      sx={{
        width: '100%',
        p: 2,
        borderRadius: 2,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: colors.csaaNeutral,
        '&:focus': {
          borderRadius: 1,
          bgcolor: colors.csaaOverlayBlack,
          outline: `solid 2px ${colors.csaaBlack}`,
        },
      }}
      key={`${account.paymentAccountToken}`}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          width: '100%',
        }}
      >
        <Stack alignItems="flex-start" sx={{ gap: 0.5 }}>
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            alignItems={{ mobile: 'flex-start', tablet: 'center' }}
            sx={{ gap: { mobile: 1, tablet: 2 } }}
          >
            <Typography
              className="csaa-qm-block"
              sx={{
                fontSize: { mobile: 19, tablet: 23 },
                lineHeight: { mobile: '24px', tablet: '32px' },
                letterSpacing: { mobile: 0.4, tablet: 0.6 },
                fontWeight: 'bold',
                color: colors.csaaBlack,
              }}
            >
              {getButtonText()}
            </Typography>
            {'card' in account && account.card?.expired && (
              <Box
                sx={{
                  backgroundColor: colors.csaaRed10,
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  height: 24,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontSize: 14,
                    lineHeight: '16px',
                    letterSpacing: 0.2,
                    color: colors.csaaDark,
                    fontWeight: 500,
                  }}
                >
                  Expired
                </Typography>
              </Box>
            )}
          </Stack>
          {'card' in account && (
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: { mobile: 16, tablet: 18 },
                lineHeight: '24px',
                letterSpacing: { mobile: 0.3, tablet: 0.4 },
                color: colors.csaaMid,
              }}
            >
              Exp {formatExpirationDate(account.card?.expirationDate)}
            </Typography>
          )}
        </Stack>
        <Box sx={{ p: 1 }}>
          <PencilEditIcon />
        </Box>
      </Stack>
    </ButtonBase>
  );
};

export default PaymentMethodOptionButton;
